import { DialogDisclosure, Heading, HeadingLevel } from "@ariakit/react";
import {
  cx,
  DocumentTitle,
  MinWidth,
  useMediaQuery,
} from "@indietabletop/appkit";
import type { ReactElement, ReactNode } from "react";
import { Link } from "wouter";
import { useSidenavStore } from "../Layout/SidenavContext.tsx";
import gitburger from "./gitburger.svg";
import { header, page } from "./style.css.ts";

export function HeaderBackButton(props: {
  backHref: string;
  theme?: "dark" | "light";
}) {
  const { backHref, theme = "dark" } = props;

  return (
    <Link href={backHref} className={header.button}>
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g
          stroke={theme === "dark" ? "#232B13" : "white"}
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M17.975 13C17.8409 13.1341 16.4309 14.3094 14.0976 16.1867C12.5543 17.4283 11.6454 18.0091 11.1222 18.4819C10.5415 19.0068 10.019 19.3977 10.0002 19.9036C9.98812 20.2292 10.4843 20.6106 11.395 21.371C12.3056 22.1313 13.7471 23.2375 15.2942 24.3605C16.8413 25.4835 17.2406 25.6571 19 27" />
          <path d="M12.1533 19.9684C12.2539 19.9684 12.3545 19.9684 15.0545 19.9851C17.7546 20.0019 23.0512 20.0354 28.5082 20.07" />
          <path d="M34.7431 10.0552C42.0265 19.6599 35.2488 30.4129 32.214 33.5449C26.6274 39.3104 15.2698 39.2868 9.95957 34.5888C2.87914 28.3249 -1.03438 21.0824 4.90146 10.0552C9.95919 0.659394 25.6389 -1.95056 34.7431 10.0552Z" />
        </g>
      </svg>
    </Link>
  );
}

export function HeaderSidenavDisclosure() {
  const store = useSidenavStore();
  return (
    <DialogDisclosure store={store} className={header.button}>
      <img src={gitburger} alt="Menu" />
    </DialogDisclosure>
  );
}

export function HeaderTitle(props: { children: string }) {
  const { children } = props;
  return <Heading className={header.title}>{children}</Heading>;
}

export function PageHeader(props: {
  children: [start: ReactElement, center: ReactElement, end?: ReactNode];
  className?: string;
}) {
  const [start, center, end] = props.children;

  return (
    <header {...cx(props, header.container)}>
      {start}
      {center}

      <div className={header.actions}>{end}</div>
    </header>
  );
}

export function PageContainer(props: {
  children: ReactNode;
  className?: string;
  withDesktopHeader?: boolean;
}) {
  const { children, withDesktopHeader } = props;

  return (
    <div {...cx(props, page.container({ withDesktopHeader }))}>{children}</div>
  );
}

export function PageMain(props: {
  children: ReactNode;
  className?: string;
  centerChildren?: boolean;
  withMobilePadding?: boolean;
}) {
  const { children, centerChildren, withMobilePadding } = props;

  return (
    <HeadingLevel>
      <main {...cx(props, page.main({ centerChildren, withMobilePadding }))}>
        {children}
      </main>
    </HeadingLevel>
  );
}

export function Page(props: {
  title: string;
  actions?: ReactNode;
  backHref: string;
  children: ReactNode;
  centerChildren?: boolean;
  withMobilePadding?: boolean;
}) {
  const isMediumOrWider = useMediaQuery(MinWidth.MEDIUM);
  const { title, actions, backHref, ...mainProps } = props;

  return (
    <HeadingLevel>
      <PageContainer>
        <DocumentTitle>{title}</DocumentTitle>

        <PageHeader>
          {!isMediumOrWider && backHref === "~/" ? (
            <HeaderSidenavDisclosure />
          ) : (
            <HeaderBackButton backHref={backHref} />
          )}

          <HeaderTitle>{title}</HeaderTitle>

          {actions}
        </PageHeader>

        <PageMain {...mainProps} />
      </PageContainer>
    </HeadingLevel>
  );
}
